import { createAltFromTitle, tokenizeContent } from "@/lib/content/tokenizer";
import { ContentToken, KeywordMap } from "@/lib/content/types";
import { Definition } from "@/types/definition-schema";
import {
  CramSheetEntry,
  RawCramSheetEntry,
  TopicCramSheets,
} from "@tsTypes/cramsheet-schema";
import { Flashcard } from "@tsTypes/flash-schema";
import { Note, SubTopicNotes } from "@tsTypes/note-schema";
import { Question } from "@tsTypes/question-schema";
import { RawStrapiVideoResponse, TopicVideos } from "@tsTypes/video-schema";

export function renderQuestionArray(questionArray: Question[]) {
  return questionArray.map((q) => renderQuestion(q));
}

export function renderFlashCardArray(flashCardArray: Flashcard[]) {
  return flashCardArray.map((card) => renderFlashCard(card));
}

export function renderDefinitionArray(definitionArray: Definition[]) {
  return definitionArray.map((def) => renderDefinition(def));
}

function tokenize(raw: string | ContentToken[]): ContentToken[] {
  return tokenizeContent(raw as unknown as string, "questions");
}

export function calculateQuestionTotalMarks(question: Question) {
  if (question.QuestionStyle[0]?.QuestionPart) {
    return question.QuestionStyle[0]?.QuestionPart.reduce(
      (acc, part) => acc + part.Marks,
      0
    );
  } else {
    return question.QuestionStyle[0]?.TotalMarks?.TotalMarks ?? 1;
  }
}

export function renderQuestion(question: Question) {
  const {
    QuestionOptions: questionOptions,
    QuestionPart: questionPart,
    AnswerExplanation: questionAnswer,
    PartAnswerExplanation: questionAnswerPart,
    MarkSchemeNotes: questionExtendedAnswerNotes,
    MarkSchemePoints: questionExtendedAnswerPoints,
  } = question.QuestionStyle[0];

  question.TotalMarks = calculateQuestionTotalMarks(question);
  question.Question = tokenize(question.Question);

  if (question.QuestionStyle[0].Options) {
    question.QuestionStyle[0].Options = question.QuestionStyle[0].Options.map(
      (option) => ({
        ...option,
        alt: createAltFromTitle(option.name, "questions") ?? option.name,
      })
    );
  }

  if (questionOptions) {
    question.QuestionStyle[0].QuestionOptions = questionOptions.map(
      (option) => ({
        id: option.id,
        Option: tokenize(option.Option),
      })
    );
  } else if (questionPart) {
    question.QuestionStyle[0].QuestionPart = questionPart.map((part) => ({
      id: part.id,
      Marks: part.Marks,
      QuestionPart: tokenize(part.QuestionPart),
    }));
  }

  if (questionAnswer) {
    question.QuestionStyle[0].AnswerExplanation = {
      id: questionAnswer.id,
      AnswerExplanation: tokenize(questionAnswer.AnswerExplanation),
    };
  } else if (questionAnswerPart) {
    question.QuestionStyle[0].PartAnswerExplanation = questionAnswerPart.map(
      (part) => ({
        id: part.id,
        AnswerExplanation: tokenize(part.AnswerExplanation),
      })
    );
  } else if (questionExtendedAnswerPoints) {
    question.QuestionStyle[0].MarkSchemePoints = tokenize(
      questionExtendedAnswerPoints
    );
    if (questionExtendedAnswerNotes) {
      question.QuestionStyle[0].MarkSchemeNotes = tokenize(
        questionExtendedAnswerNotes
      );
    }
  }

  if (question.UsefulTip) {
    question.UsefulTip = tokenize(question.UsefulTip);
  }

  return question;
}

export function renderFlashCard(card: Flashcard) {
  card.Front = tokenizeContent(card.Front as unknown as string, "flashcards");
  card.Back = tokenizeContent(card.Back as unknown as string, "flashcards");
  return card;
}

export function renderDefinition(definition: Definition) {
  definition.Definition = tokenizeContent(
    definition.Definition as unknown as string,
    "definitions"
  );
  return definition;
}

export function renderVideos(
  videos: RawStrapiVideoResponse,
  subTopicDocumentIds: string[],
  keywordMap: KeywordMap
): TopicVideos {
  const includedSubTopics: string[] = [];
  return subTopicDocumentIds.reduce((acc: TopicVideos, documentId) => {
    if (includedSubTopics.includes(documentId)) {
      return acc;
    }
    const relevantVideos = videos.filter((video) =>
      video.RelatedSubTopics.some((st) => st.documentId === documentId)
    );
    includedSubTopics.push(
      ...relevantVideos.flatMap((video) =>
        video.RelatedSubTopics.map((st) => st.documentId)
      )
    );
    if (relevantVideos.length === 0) {
      acc[documentId] = null;
    } else {
      acc[documentId] = relevantVideos.map((video) => ({
        ...video,
        VideoDescription: tokenizeContent(
          video.VideoDescription as unknown as string,
          "videos",
          keywordMap
        ),
      }));
    }
    return acc;
  }, {});
}

export function renderCramSheets(
  data: RawCramSheetEntry[],
  subTopicDocumentIds: string[],
  keywordMap: KeywordMap
): TopicCramSheets {
  const includedSubTopics: string[] = [];
  return subTopicDocumentIds.reduce((acc: TopicCramSheets, documentId) => {
    if (includedSubTopics.includes(documentId)) {
      return acc;
    }
    const relevantEntries = data.filter(
      (cramSheet, index, entries) =>
        cramSheet.RelatedSubTopics.some((st) => st.documentId === documentId) &&
        entries.findIndex((entry) => entry.documentId === cramSheet.documentId) ===
          index
    );
    includedSubTopics.push(
      ...relevantEntries.flatMap((cramSheet) =>
        cramSheet.RelatedSubTopics.map((st) => st.documentId)
      )
    );
    if (relevantEntries.length === 0) {
      acc[documentId] = null;
    } else {
      acc[documentId] = relevantEntries.map((cramSheet) => ({
        ...cramSheet,
        subTopicDescription: tokenizeContent(
          cramSheet.RelatedSubTopics[0]?.SubTopicDescription ?? "",
          "cram sheets",
          keywordMap
        ),
      })) as CramSheetEntry[];
    }
    return acc;
  }, {});
}

export function renderNote(note: Note, keywordMap: KeywordMap) {
  note.NoteSection.forEach((noteSection) => {
    noteSection.Notes = tokenizeContent(
      noteSection.Notes as unknown as string,
      "notes",
      keywordMap
    );
  });
}

export function renderNoteArray(notes: Note[], keywordMap: KeywordMap): Note[] {
  notes.forEach((note) => {
    renderNote(note, keywordMap);
  });
  return notes;
}

/** Maps Strapi notes to per-sub-topic entries and tokenizes note body HTML (like `renderVideos`). */
export function renderNotesForSubTopics(
  notes: Note[],
  subTopicDocumentIds: string[],
  keywordMap: KeywordMap
): SubTopicNotes[] {
  return subTopicDocumentIds.map((subTopicId) => {
    const note = notes.find((n) => n.RelatedSubTopic.documentId === subTopicId);
    if (note) renderNote(note, keywordMap);
    return {
      documentId: subTopicId,
      note,
    } as SubTopicNotes;
  });
}
